// src/views/parent/PAMessages.jsx
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useAuth } from "../../context/AuthContext";
import { useParentDrawer } from "../../context/ParentDrawerContext";
import { urlimage } from "../../utils/config";
import Swal from "sweetalert2";

export default function PAMessages({ slug }) {
  const navigate = useNavigate();
  const {
    getMessagesAdministration,
    envoyerMessageAdministration,
    modifierMessageAdministration,
    supprimerMessageAdministration,
    getMessagesNonLus,
  } = useParentService();
  const { user } = useAuth();
  const { openDrawer } = useParentDrawer();

  const scrollContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const [messages, setMessages] = useState([]);
  const [contenu, setContenu] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [pieceJointe, setPieceJointe] = useState(null);
  const [pieceJointeNom, setPieceJointeNom] = useState(null);
  const [pieceJointeSize, setPieceJointeSize] = useState(0);
  const [editingMessage, setEditingMessage] = useState(null);
  const [badges, setBadges] = useState({ messages: 0 });

  // ─── SCROLL ────────────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, []);

  // ─── CHARGEMENT ─────────────────────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    try {
      const [msgData, badgesData] = await Promise.all([
        getMessagesAdministration(slug),
        getMessagesNonLus(slug).catch(() => ({ non_lus: 0 })),
      ]);

      setMessages(msgData || []);
      setBadges({ messages: badgesData?.non_lus ?? 0 });
      setError(null);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      setError("Impossible de charger les messages.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, scrollToBottom]);

  // Chargement initial + polling toutes les 15 s
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (!slug) return;
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [slug, fetchMessages]);

  // ─── ENVOI / MODIFICATION ───────────────────────────────────────────
  const handleSendMessage = async () => {
    if (!contenu.trim() && !pieceJointe) {
      Swal.fire({
        icon: "warning",
        title: "Message vide",
        text: "Veuillez écrire un message ou joindre un fichier",
        confirmButtonColor: "var(--pr)",
        toast: true,
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
      });
      return;
    }

    setSending(true);
    try {
      if (editingMessage) {
        await modifierMessageAdministration(slug, editingMessage.id, contenu);
        Swal.fire({
          icon: "success",
          title: "Message modifié avec succès",
          toast: true,
          timer: 2000,
          showConfirmButton: false,
          position: "top-right",
        });
        setEditingMessage(null);
      } else {
        await envoyerMessageAdministration(slug, contenu, pieceJointe);
        Swal.fire({
          icon: "success",
          title: "Message envoyé avec succès !",
          toast: true,
          timer: 2000,
          showConfirmButton: false,
          position: "top-right",
        });
      }

      setContenu("");
      setPieceJointe(null);
      setPieceJointeNom(null);
      setPieceJointeSize(0);

      await fetchMessages();

      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Impossible d'envoyer le message",
        text: err.response?.data?.message || "Vérifiez votre connexion et réessayez",
        confirmButtonColor: "var(--pr)",
      });
    } finally {
      setSending(false);
    }
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setContenu("");
  };

  const handleDeleteMessage = async (id) => {
    const result = await Swal.fire({
      title: "Supprimer le message ?",
      text: "Cette action est irréversible !",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "var(--pr)",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    });

    if (!result.isConfirmed) return;

    try {
      await supprimerMessageAdministration(slug, id);
      Swal.fire({
        icon: "success",
        title: "Message supprimé",
        toast: true,
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
      });
      fetchMessages();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Impossible de supprimer le message",
        confirmButtonColor: "var(--pr)",
      });
    }
  };

  const startEdit = (msg) => {
    setEditingMessage(msg);
    setContenu(msg.contenu);
    textareaRef.current?.focus();
  };

  // ─── HELPERS DATES ─────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const heure = `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;

    if (msgDay.getTime() === today.getTime()) {
      return `Aujourd'hui ${heure}`;
    }
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (msgDay.getTime() === yesterday.getTime()) {
      return `Hier ${heure}`;
    }
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()} ${heure}`;
  };

  const formatDateHeader = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const jours = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
    return `${jours[date.getDay() - 1] || ""} ${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const showDateHeader = (index) => {
    if (index === 0) return true;
    const curr = messages[index]?.date_envoi;
    const prev = messages[index - 1]?.date_envoi;
    if (!curr || !prev) return false;
    const currDate = new Date(curr).toDateString();
    const prevDate = new Date(prev).toDateString();
    return currDate !== prevDate;
  };

  const isMine = (msg) => {
    if (user && msg.expediteur?.username) {
      return msg.expediteur.username === user.username;
    }
    return !msg.destinataire;
  };

  // ─── HELPERS FICHIERS ──────────────────────────────────────────────
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFullUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${urlimage}${path}`;
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const exts = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
    return exts.some((ext) => fileName.toLowerCase().endsWith(ext));
  };

  const isVideoFile = (fileName) => {
    if (!fileName) return false;
    const exts = [".mp4", ".mov", ".avi", ".mkv", ".webm", ".3gp", ".m4v"];
    return exts.some((ext) => fileName.toLowerCase().endsWith(ext));
  };

  const getFileIconClass = (fileName) => {
    if (!fileName) return "ti-file";
    const name = fileName.toLowerCase();
    if (isVideoFile(name)) return "ti-file-video";
    if (name.endsWith(".pdf")) return "ti-file-type-pdf";
    if (name.endsWith(".doc") || name.endsWith(".docx")) return "ti-file-type-doc";
    if (name.endsWith(".xls") || name.endsWith(".xlsx")) return "ti-file-spreadsheet";
    if (name.endsWith(".ppt") || name.endsWith(".pptx")) return "ti-presentation";
    if (name.endsWith(".mp3") || name.endsWith(".wav")) return "ti-file-music";
    if (name.endsWith(".zip") || name.endsWith(".rar")) return "ti-file-zip";
    return "ti-file";
  };

  const getFileName = (url) => {
    if (!url) return "Fichier joint";
    const parts = url.split("/");
    let fileName = parts[parts.length - 1];
    const queryIndex = fileName.indexOf("?");
    if (queryIndex !== -1) fileName = fileName.substring(0, queryIndex);
    if (fileName.length > 20) {
      const ext = fileName.split(".").pop();
      const name = fileName.substring(0, 15);
      return `${name}...${ext}`;
    }
    return fileName;
  };

  const openFile = (url) => window.open(getFullUrl(url), "_blank");

  const pickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPieceJointe(file);
      setPieceJointeNom(file.name);
      setPieceJointeSize(file.size);
    }
  };

  const removeFile = () => {
    setPieceJointe(null);
    setPieceJointeNom(null);
    setPieceJointeSize(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ─── RENDU PIÈCE JOINTE REÇUE ───────────────────────────────────────
  const renderReceivedFile = (fileUrl) => {
    if (!fileUrl) return null;
    const fullUrl = getFullUrl(fileUrl);
    const fileName = getFileName(fullUrl);

    if (isImageFile(fullUrl)) {
      return (
        <img
          className="file-img"
          src={fullUrl}
          alt="Pièce jointe"
          onClick={() => openFile(fileUrl)}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      );
    }

    if (isVideoFile(fullUrl)) {
      return (
        <div className="file-video-box" onClick={() => openFile(fileUrl)}>
          <i className="ti ti-player-play" />
          <span>{fileName}</span>
        </div>
      );
    }

    return (
      <button className="file-doc-btn" onClick={() => openFile(fileUrl)} type="button">
        <i className={`ti ${getFileIconClass(fileName)}`} />
        <div className="file-doc-info">
          <span className="file-doc-name">{fileName}</span>
          <span className="file-doc-type">Document</span>
        </div>
        <i className="ti ti-external-link" />
      </button>
    );
  };

  // URL locale du preview de fichier (nettoyée à chaque changement)
  const previewUrl = useMemo(() => {
    if (pieceJointe && isImageFile(pieceJointeNom)) {
      return URL.createObjectURL(pieceJointe);
    }
    return null;
  }, [pieceJointe, pieceJointeNom]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .messages-page {
          height: 100vh;
          background: var(--s2);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .messages-header {
          flex-shrink: 0;
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 20px 16px;
          border-radius: 0 0 40px 40px;
          box-shadow: 0 4px 14px rgba(0,0,0,0.15);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .messages-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .messages-header .menu-btn {
          position: absolute;
          top: 8px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          width: 42px;
          height: 42px;
          cursor: pointer;
          color: #fff;
          font-size: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .messages-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .messages-header .header-icon {
          font-size: 44px;
          color: #fff;
          margin-bottom: 6px;
        }

        .messages-header h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 21px;
          font-weight: 700;
          margin: 0;
        }

        .messages-header p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.75);
          font-size: 13px;
          margin: 4px 0 0;
        }

        .messages-header .unread-pill {
          margin-top: 8px;
          display: inline-block;
          padding: 2px 12px;
          background: rgba(255,255,255,0.15);
          border: 1px solid var(--or);
          border-radius: 20px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: #fff;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 12px 16px 4px;
        }

        .chat-messages .empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--h);
          text-align: center;
        }

        .chat-messages .empty i {
          font-size: 64px;
          opacity: 0.4;
          margin-bottom: 12px;
        }

        .chat-messages .empty h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: var(--m);
          margin: 0 0 6px;
        }

        .chat-messages .empty p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          margin: 0;
          line-height: 1.4;
        }

        .date-header {
          text-align: center;
          margin: 8px 0 14px;
        }

        .date-header span {
          background: var(--b2);
          padding: 6px 14px;
          border-radius: 20px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: var(--m);
        }

        .message-row {
          display: flex;
          margin-bottom: 12px;
        }

        .message-row.mine { justify-content: flex-end; }
        .message-row.other { justify-content: flex-start; }

        .message-bubble {
          max-width: 75%;
          min-width: 140px;
          padding: 10px 14px;
          border-radius: 16px;
          word-break: break-word;
          white-space: pre-wrap;
          box-shadow: 0 1px 5px rgba(0,0,0,0.05);
        }

        .message-bubble.mine {
          background: var(--pr);
          color: #fff;
          border-radius: 16px 16px 4px 16px;
        }

        .message-bubble.other {
          background: #fff;
          color: var(--t);
          border-radius: 16px 16px 16px 4px;
        }

        .message-bubble .meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }

        .message-bubble .meta .sender {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 600;
        }

        .message-bubble.mine .meta .sender { color: var(--or-lt); }
        .message-bubble.other .meta .sender { color: var(--pr); }

        .message-bubble .meta .time {
          font-family: 'Inter', sans-serif;
          font-size: 9px;
          opacity: 0.6;
          white-space: nowrap;
        }

        .message-bubble .content {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          line-height: 1.4;
        }

        .message-bubble .file-img {
          width: 100%;
          height: 180px;
          border-radius: 12px;
          object-fit: cover;
          margin-top: 6px;
          cursor: pointer;
          display: block;
        }

        .message-bubble .file-video-box {
          margin-top: 6px;
          background: #1f2937;
          border-radius: 12px;
          height: 180px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          color: #fff;
        }

        .message-bubble .file-video-box i { font-size: 40px; }
        .message-bubble .file-video-box span {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
        }

        .message-bubble .file-doc-btn {
          margin-top: 6px;
          width: 100%;
          padding: 10px;
          background: rgba(0,0,0,0.04);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          color: inherit;
        }

        .message-bubble.mine .file-doc-btn {
          background: rgba(255,255,255,0.15);
        }

        .message-bubble .file-doc-btn i:first-child { font-size: 22px; }

        .message-bubble .file-doc-info {
          flex: 1;
          text-align: left;
          display: flex;
          flex-direction: column;
        }

        .message-bubble .file-doc-name {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
        }

        .message-bubble .file-doc-type {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          opacity: 0.7;
        }

        .message-bubble .msg-actions {
          display: flex;
          justify-content: flex-end;
          gap: 14px;
          margin-top: 6px;
        }

        .message-bubble .msg-actions button {
          background: none;
          border: none;
          cursor: pointer;
          color: rgba(255,255,255,0.75);
          padding: 2px;
          font-size: 13px;
        }

        .message-bubble .msg-actions button:hover { color: #fff; }

        .input-card {
          flex-shrink: 0;
          margin: 12px;
          padding: 10px 12px;
          background: #fff;
          border-radius: 24px;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.08);
        }

        .edit-banner {
          margin-bottom: 8px;
          padding: 6px 12px;
          background: #D1ECF1;
          border-radius: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .edit-banner span {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: #0c5460;
        }

        .edit-banner button {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--m);
          display: flex;
          align-items: center;
        }

        .input-row {
          display: flex;
          align-items: flex-end;
          gap: 8px;
        }

        .input-row textarea {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 20px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          resize: none;
          outline: none;
          min-height: 40px;
          max-height: 80px;
          background: var(--s2);
          transition: background 0.2s;
        }

        .input-row textarea:focus { background: var(--pr-bg); }

        .input-row .btn-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .input-row .btn-icon.attach {
          background: var(--s2);
          color: var(--m);
        }

        .input-row .btn-icon.attach.active { color: var(--or-dk); }
        .input-row .btn-icon.attach:hover { background: var(--b); }

        .input-row .btn-icon.send {
          background: var(--pr);
          color: #fff;
        }

        .input-row .btn-icon.send:hover:not(:disabled) { background: var(--pr-lt); }
        .input-row .btn-icon.send:disabled {
          opacity: 0.5;
          cursor: default;
        }

        .file-preview {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px;
          background: var(--pr-bg);
          border: 1px solid var(--b2);
          border-radius: 12px;
          margin-top: 8px;
        }

        .file-preview .file-preview-thumb {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
          background: var(--b2);
        }

        .file-preview .file-preview-icon {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          background: var(--b2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 20px;
          color: var(--pr);
        }

        .file-preview .file-preview-info {
          flex: 1;
          min-width: 0;
        }

        .file-preview .file-preview-name {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: var(--t);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .file-preview .file-preview-size {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          color: var(--m);
        }

        .file-preview .remove {
          cursor: pointer;
          color: var(--rd);
          background: rgba(220,53,69,0.1);
          border: none;
          border-radius: 50%;
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
        }

        /* Loader intégré dans la zone de contenu */
        .messages-loading {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
        }

        .messages-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: msg-spin 0.8s linear infinite;
        }

        @keyframes msg-spin { to { transform: rotate(360deg); } }

        .messages-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        /* Carte d'erreur */
        .messages-error {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 10px;
          text-align: center;
        }

        .messages-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }

        .messages-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px;
          font-weight: 600;
          color: var(--t);
          margin: 4px 0 0;
        }

        .messages-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 12px;
        }

        .messages-error .actions-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .messages-error .btn {
          border: none;
          border-radius: 30px;
          padding: 10px 22px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          background: var(--pr);
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .messages-error .btn:hover { opacity: 0.9; }
        .messages-error .btn.btn-secondary {
          background: var(--b2);
          color: var(--t);
        }

        @media (max-width: 480px) {
          .messages-header { padding: 16px 12px; }
          .messages-header h1 { font-size: 18px; }
          .chat-messages { padding: 8px 10px 4px; }
          .message-bubble { max-width: 88%; padding: 8px 12px; }
          .input-card { margin: 8px; padding: 8px 10px; }
          .input-row textarea {
            font-size: 12px;
            padding: 8px 14px;
            min-height: 34px;
          }
          .input-row .btn-icon { width: 34px; height: 34px; font-size: 14px; }
        }
      `}</style>

      <div className="messages-page">
        {/* Header TOUJOURS affiché */}
        <div className="messages-header">
          <button className="menu-btn" onClick={openDrawer} aria-label="Ouvrir le menu">
            <i className="ti ti-menu-2" />
          </button>

          <i className="ti ti-message-2 header-icon" />
          <h1>Messagerie</h1>
          <p>Échangez avec l'administration</p>
          {!loading && !error && badges.messages > 0 && (
            <span className="unread-pill">
              {badges.messages} message{badges.messages > 1 ? "s" : ""} non lu{badges.messages > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {loading ? (
          /* ── ÉTAT CHARGEMENT ── */
          <div className="messages-loading">
            <div className="spinner" />
            <span>Chargement des messages…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="messages-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button className="btn" onClick={fetchMessages}>
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <>
            {/* Fil de discussion */}
            <div className="chat-messages" ref={scrollContainerRef}>
              {messages.length === 0 ? (
                <div className="empty">
                  <i className="ti ti-message-2" />
                  <h3>Aucun message</h3>
                  <p>Envoyez un message à l'administration<br />pour commencer.</p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const mine = isMine(msg);
                  return (
                    <div key={msg.id}>
                      {showDateHeader(index) && (
                        <div className="date-header">
                          <span>{formatDateHeader(msg.date_envoi)}</span>
                        </div>
                      )}
                      <div className={`message-row ${mine ? "mine" : "other"}`}>
                        <div className={`message-bubble ${mine ? "mine" : "other"}`}>
                          <div className="meta">
                            <span className="sender">
                              <i className="ti ti-user-circle" />
                              {(mine ? (user?.username || "Vous") : (msg.expediteur?.nom || msg.expediteur?.username || "Administration"))}
                              {mine ? " (Vous)" : ""}
                            </span>
                            <span className="time">{formatDate(msg.date_envoi)}</span>
                          </div>
                          <div className="content">{msg.contenu}</div>
                          {(msg.piece_jointe_url || msg.piece_jointe) &&
                            renderReceivedFile(msg.piece_jointe_url || msg.piece_jointe)}
                          {mine && (
                            <div className="msg-actions">
                              <button onClick={() => startEdit(msg)} title="Modifier">
                                <i className="ti ti-edit" />
                              </button>
                              <button onClick={() => handleDeleteMessage(msg.id)} title="Supprimer">
                                <i className="ti ti-trash" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Carte de saisie flottante */}
            <div className="input-card">
              {editingMessage && (
                <div className="edit-banner">
                  <span>Modification en cours...</span>
                  <button onClick={cancelEdit}>
                    <i className="ti ti-x" />
                  </button>
                </div>
              )}

              <div className="input-row">
                <textarea
                  ref={textareaRef}
                  value={contenu}
                  onChange={(e) => setContenu(e.target.value)}
                  placeholder="Écrivez votre message..."
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  className={`btn-icon attach ${pieceJointe ? "active" : ""}`}
                  onClick={pickFile}
                  aria-label="Joindre un fichier"
                >
                  <i className="ti ti-paperclip" />
                </button>
                <button
                  className="btn-icon send"
                  onClick={handleSendMessage}
                  disabled={(!contenu.trim() && !pieceJointe) || sending}
                  aria-label="Envoyer"
                >
                  {sending ? (
                    <div className="loader-spinner" style={{ width: 18, height: 18, border: "2px solid #fff", borderTopColor: "transparent" }} />
                  ) : editingMessage ? (
                    <i className="ti ti-check" />
                  ) : (
                    <i className="ti ti-send" />
                  )}
                </button>
              </div>
              <input ref={fileInputRef} type="file" onChange={handleFileChange} style={{ display: "none" }} />
              {pieceJointe && (
                <div className="file-preview">
                  {previewUrl ? (
                    <img className="file-preview-thumb" src={previewUrl} alt="Aperçu" />
                  ) : (
                    <div className="file-preview-icon">
                      <i className={`ti ${getFileIconClass(pieceJointeNom)}`} />
                    </div>
                  )}
                  <div className="file-preview-info">
                    <div className="file-preview-name">{pieceJointeNom}</div>
                    <div className="file-preview-size">{formatFileSize(pieceJointeSize)}</div>
                  </div>
                  <button className="remove" onClick={removeFile} aria-label="Retirer le fichier">
                    <i className="ti ti-x" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}